import {
    Button,
    Checkbox,
    Group,
    Select,
    Space,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import {AppUser, PasskeyService, UserAdd, UserService} from '../../client';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {DataTable, DataTableSortStatus} from 'mantine-datatable';
import sortBy from 'lodash/sortBy';
import React, {useEffect, useState} from "react";
import {useAuthStore} from "../../store/authStore";
import {jwtDecode} from "jwt-decode";
import {closeModal, openModal} from '@mantine/modals';
import {DateInput} from "@mantine/dates";
import {formatAdminDate} from '../../utils/formatDate';
import {useDataTablePagination} from '../../utils/useDataTablePagination';
import {
    canManageUsers,
    canModifyUser,
    getAssignableRoles,
    normalizeRole,
    ROLE_LABELS,
    type UserRole,
} from '../../utils/roles';
import {ChangePasswordModal} from './ChangePasswordModal';
import {RoleAccessTable} from './RoleAccessTable';
import {PageLayout} from '../shell/PageLayout';
import {
    ActiveUntilAction,
    BooleanStatusIcon,
    ChangePasswordAction,
    CurrentUserIndicator,
    DeleteTableAction,
    PasskeyResetAction,
    ToggleUserActiveAction,
} from '../common/TableActionIcons';

const ADMIN_DATE_FORMAT = 'YYYY-MM-DD';

export const Users = () => {
        const {isLoading, data} = useQuery({
            queryKey: ['users'],
            queryFn: UserService.getAllUsersApiV1UsersGet,
        });

        const [users, setUsers] = useState<AppUser[]>([]);
        const queryClient = useQueryClient();

        const token = useAuthStore(state => state.token);
        const currentUser: string = jwtDecode(token).sub ?? '';


        const [isPasswordChangeModalOpen, setIsPasswordChangeModalOpen] = React.useState(false);
        const [changePasswordUser, setChangePasswordUser] = React.useState('');

        const {data: currentUserProfile} = useQuery({
            queryKey: ['current-user'],
            queryFn: UserService.getCurrentUserApiV1UserMeGet,
        });

        const isAdmin = canManageUsers(normalizeRole(currentUserProfile?.role));
        const actorRole = normalizeRole(currentUserProfile?.role);
        const assignableRoles = getAssignableRoles(actorRole);
        const roleSelectData = assignableRoles.map((role) => ({
            value: role,
            label: ROLE_LABELS[role],
        }));

        const deleteAppUser = (user: AppUser) => {
            UserService.deleteUserByUsernameApiV1UserUsernameDelete({
                username: user.username
            }).then(() => {
                queryClient.invalidateQueries({queryKey: ['users']}).then(r => closeModal("delete"));
            });
        }

        const setActiveUntil = (user: AppUser) => {
            if (!user.active_until) {
                return;
            }
            UserService.updateDeactivationDateForUserApiV1UserChangeDateUseridNewDatePut({
                userid: user.id,
                newDate: new Date(user.active_until).toISOString().split('T')[0]
            }).then(() => {
                queryClient.invalidateQueries({queryKey: ['users']}).then(() => closeModal("activeUntil"));
                ;
            });
        };

        const addUser = (user: UserAdd) => {
            if (!user.username || !user.email || !user.password) {
                return;
            }
            UserService.addUserApiV1UserPost({
                requestBody: user
            }).then(() => {
                queryClient.invalidateQueries({queryKey: ['users']}).then(() => closeModal("add"));
            });
        }

        const updateUserRole = (username: string, role: UserRole) => {
            UserService.updateUserRoleApiV1UserUsernameRolePut({
                username,
                requestBody: {role},
            }).then(() => {
                queryClient.invalidateQueries({queryKey: ['users']});
            });
        };

        const showAddModal = ({user, action}: { user: UserAdd; action: 'add' }) => {
            if (!user.role) {
                user.role = 'operator';
            }
            openModal({
                modalId: action,
                title: 'Add User',
                children: (
                    <>
                        <TextInput label="Username" required placeholder="username" onChange={(e) => {
                            user.username = e.target.value;
                        }}/>
                        <TextInput label="E-mail" required placeholder="e@mail.com" onChange={(e) => {
                            user.email = e.target.value;
                        }}/>
                        <TextInput label="Password" required placeholder="password" type="password" onChange={(e) => {
                            user.password = e.target.value;
                        }}/>
                        <Select
                            label="Role"
                            data={roleSelectData}
                            defaultValue="operator"
                            onChange={(value) => {
                                user.role = (value ?? 'operator') as UserRole;
                            }}
                        />
                        <Group m="xs">
                            <Checkbox label="Active" defaultChecked onChange={(e) => {
                                user.is_active = e.target.checked;
                            }}/>
                        </Group>
                        <DateInput
                            label="Active until"
                            placeholder="Select date"
                            valueFormat={ADMIN_DATE_FORMAT}
                            onChange={(date) => {
                                user.active_until = date ?? '';
                            }}
                        />
                        <Button type="submit" variant="outline" mt="xs" onClick={() => addUser(user)}>
                            Add
                        </Button>
                    </>
                ),
            });
        };

        const resetPasskey = (user: AppUser) => {
            PasskeyService.resetPasskeyForUserApiV1PasskeyUserUsernameDelete({
                username: user.username,
            }).then(() => {
                queryClient.invalidateQueries({queryKey: ['users']});
                closeModal('resetPasskey');
            });
        };

        const showResetPasskeyModal = (user: AppUser) => {
            openModal({
                modalId: 'resetPasskey',
                title: 'Reset passkey',
                children: (
                    <>
                        <p>
                            Delete passkey for <strong>{user.username}</strong>?
                            The user can sign in with a password again afterwards.
                        </p>
                        <Stack gap="xs">
                            <Button color="red" onClick={() => resetPasskey(user)}>Yes, reset</Button>
                            <Button onClick={() => closeModal('resetPasskey')}>Cancel</Button>
                        </Stack>
                    </>
                ),
            });
        };

        const changePasswordButton = (record: AppUser) => {
            if (record.has_passkey) {
                return (
                    <Text size="xs" c="dimmed">
                        Passkey active
                    </Text>
                );
            }
            return (
                <ChangePasswordAction
                    onClick={() => {
                        setChangePasswordUser(record.username);
                        setIsPasswordChangeModalOpen(true);
                    }}
                />
            );
        };


        const showModal = ({user, action}: { user: AppUser; action: 'activeUntil' | 'delete' }) => {
            openModal({
                modalId: action,
                title:
                    action === 'activeUntil'
                        ? 'Change active until'
                        : action === 'delete'
                            ? 'Delete user'
                            : '',
                children: (
                    (action === 'activeUntil' && <>
                        <DateInput
                            label="Active until"
                            required
                            placeholder="Select date"
                            valueFormat={ADMIN_DATE_FORMAT}
                            onChange={(date) => {
                                user.active_until = date ?? '';
                            }}
                            value={user.active_until ?? undefined}
                        />
                        <Button type="submit" variant="outline" mt="xs" onClick={() => setActiveUntil(user)}>
                            Yes, change
                        </Button>
                    </>)
                    || (action === 'delete' && <>
                        <p>Are you sure you want to delete <strong>{user.username}</strong>?</p>
                        <Stack gap="xs">
                            <Button color="red" onClick={() => deleteAppUser(user)}>Yes</Button>
                            <Button onClick={() => closeModal(action)}>No</Button>
                        </Stack>
                    </>)
                ),
            });
        };


        const toggleActive = (user: AppUser) => {
            UserService.toggleActiveUserApiV1UserToggleActiveUseridPut({
                userid: user.id
            }).then(() => {
                queryClient.invalidateQueries({queryKey: ['users']}).then(r => {
                });
            });
        }

        const [sortStatus, setSortStatus] = useState<DataTableSortStatus<AppUser>>({
            columnAccessor: 'username',
            direction: 'desc',
        });

        const {page, recordsPerPage, paginate, getPaginationProps} = useDataTablePagination();
        const [records, setRecords] = useState<AppUser[]>([]);

        useEffect(() => {
            const sorted = sortBy(data, sortStatus.columnAccessor) as AppUser[];
            const tmp = sortStatus.direction === 'desc' ? sorted.reverse() : sorted;
            setUsers(tmp);
            setRecords(paginate(tmp));
        }, [sortStatus, data, page, recordsPerPage, paginate]);

        if (!isAdmin) {
            return <></>;
        }

        return (
            <PageLayout title="Users" description="Manage admin accounts and access.">
                <DataTable
                    mt={10}
                    withTableBorder
                    striped
                    highlightOnHover
                    records={records}
                    fetching={isLoading}
                    loaderType='oval'
                    sortStatus={sortStatus}
                    onSortStatusChange={setSortStatus}
                    {...getPaginationProps(users.length)}
                    columns={[
                        {accessor: 'username', title: 'Username', sortable: true},
                        {
                            accessor: 'active',
                            title: 'Active',
                            render: ({is_active}) => (
                                <BooleanStatusIcon
                                    value={Boolean(is_active)}
                                    trueLabel="Active"
                                    falseLabel="Inactive"
                                    falseVariant="blocked"
                                />
                            ),
                            sortable: true,
                        },
                        {
                            accessor: 'role',
                            title: 'Role',
                            render: (record) => {
                                const role = normalizeRole(record.role ?? (record.is_admin ? 'admin' : 'operator'));
                                if (!canModifyUser(actorRole, role, currentUser, record.username)) {
                                    return <Text size="sm">{ROLE_LABELS[role]}</Text>;
                                }
                                return (
                                    <Select
                                        size="xs"
                                        data={roleSelectData}
                                        value={role}
                                        onChange={(value) => {
                                            if (value) {
                                                updateUserRole(record.username, value as UserRole);
                                            }
                                        }}
                                        allowDeselect={false}
                                        comboboxProps={{withinPortal: true}}
                                    />
                                );
                            },
                            sortable: true,
                        },
                        {
                            accessor: 'active_until',
                            title: 'Active Until',
                            render: ({active_until}) => formatAdminDate(active_until),
                        },
                        {
                            accessor: 'last_login',
                            title: 'Last Login',
                            sortable: true,
                            render: ({last_login}) => formatAdminDate(last_login),
                        },
                        {
                            accessor: 'has_passkey',
                            title: 'Passkey',
                            render: ({has_passkey}) => (
                                <BooleanStatusIcon
                                    value={Boolean(has_passkey)}
                                    trueLabel="Passkey registered"
                                    falseLabel="No passkey"
                                    falseVariant="neutral"
                                />
                            ),
                        },
                        {
                            accessor: 'actions',
                            title: '',
                            render: (record) => {
                                const targetRole = normalizeRole(record.role ?? (record.is_admin ? 'admin' : 'operator'));
                                if (!canModifyUser(actorRole, targetRole, currentUser, record.username)) {
                                    if (record.username === currentUser) {
                                        return (
                                            <Group gap="xs" wrap="nowrap">
                                                <CurrentUserIndicator/>
                                                {changePasswordButton(record)}
                                            </Group>
                                        );
                                    }
                                    return null;
                                }

                                return (
                                    <Group gap="xs" wrap="nowrap">
                                        <DeleteTableAction
                                            onClick={() => showModal({user: record, action: 'delete'})}
                                            label="Delete user"
                                        />
                                        <ActiveUntilAction
                                            onClick={() => showModal({user: record, action: 'activeUntil'})}
                                        />
                                        <ToggleUserActiveAction
                                            isActive={Boolean(record.is_active)}
                                            onClick={() => toggleActive(record)}
                                        />
                                        {record.has_passkey ? (
                                            <PasskeyResetAction
                                                onClick={() => showResetPasskeyModal(record)}
                                            />
                                        ) : null}
                                        {record.is_active ? changePasswordButton(record) : null}
                                    </Group>
                                );
                            },
                        },
                    ]}
                />
                <Group mt={10}>
                    <Button onClick={() => showAddModal({user: {role: 'operator'} as UserAdd, action: 'add'})}>Add User</Button>
                    <Space/>
                </Group>
                <RoleAccessTable/>
                <ChangePasswordModal
                    opened={isPasswordChangeModalOpen}
                    onClose={() => {
                        setIsPasswordChangeModalOpen(false);
                        setChangePasswordUser('');
                    }}
                    username={changePasswordUser}
                />
            </PageLayout>
        );
    }
;
